import { chain, compact, startCase, noop, keyBy } from "lodash"
import { VALUE_FORMATS } from "./constants"
import { METRIC_OPTIONS } from "./definitions"
import { reorderByCategory, makeTooltipExtendedText } from "./utils"

function getCategoryName(category, modelMetadata, metricCategory, forecasts = {}, classifications = {}) {
  if (category === "impliedReturns") {
    return "Implied Returns"
  }

  const forecastCategory = forecasts[category]
  if (forecastCategory) {
    return forecastCategory.name
  }

  const classificationCategory = classifications[metricCategory]
  if (classificationCategory) {
    return classificationCategory.name
  }

  const categoryMetadata = modelMetadata.categories.find((c) => c.id === category)
  const categoryLabel = categoryMetadata?.name || startCase(category)

  // If both labels are the same, we don't want to have a duplicate label - e.g. "Descriptors (DESCRIPTORS)"
  if (metricCategory && metricCategory.toLowerCase() !== categoryLabel.toLowerCase()) {
    return `${categoryLabel} (${metricCategory})`
  }
  return categoryLabel
}

const customInstrumentFilters = {
  performance: ({ isEndVsStartDateComparison }) => getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
  descriptors: ({ format }) => (instrumentColumns) => format === VALUE_FORMATS.STRING ? instrumentColumns.filter(column => column !== "delta") : instrumentColumns,
}

export function getCustomSelectedMetrics(selectedMetrics, flattenedMetrics, modelMetadata, instrumentsWithMetadata, forecasts, classifications, isEndVsStartDateComparison) {
  if (!selectedMetrics) {
    return []
  }
  const customMetrics = selectedMetrics.map((metricId) => {
    const metric = flattenedMetrics?.[metricId] ?? {}
    const { dataPath, category, label, format, metricCategory, prefix } = metric
    const categoryName = getCategoryName(category, modelMetadata, metricCategory, keyBy(forecasts, "id"), keyBy(classifications, "id"))
    const filterInstrumentColumns = customInstrumentFilters[prefix] || customInstrumentFilters[metric.category] || noop

    return ({
      id: metricId,
      label: label || metricId,
      category: categoryName,
      dataPath,
      prefix: metric.prefix,
      format: format || VALUE_FORMATS.NUMBER,
      filterInstrumentColumns: filterInstrumentColumns({ isEndVsStartDateComparison, format }),
      tooltipText: (instrumentType) => makeTooltipExtendedText(dataPath, instrumentType, metric, instrumentsWithMetadata),
    })
  })
  return reorderByCategory(customMetrics)
}

export function getContentSetMetrics(contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay) {
  if (!contentSetId) {
    return []
  }

  // Filter flattened metrics to only include keys that start with the contentSetId
  // and then transform that metric to have the correct metadata
  const filteredMetrics = Object.entries(flattenedMetrics)
    .reduce((acc, [key, value]) => {
      const [prefix, category, factor, metricDisplay] = key.split(".")
      if (prefix !== contentSetId || !value.category || !selectedCategory) {
        return acc
      }

      const doesCategoryMatch = value.category.toLowerCase() === selectedCategory.toLowerCase()

      let dataKey
      if (!selectedMetricDisplay) {
        dataKey = key
      } else if (selectedMetricDisplay === metricDisplay) {
        dataKey = compact([prefix, category, factor]).join(".")
      }

      if (dataKey && doesCategoryMatch && !acc[dataKey]) {
        const label = chain(value.label)
          .split(" (")
          .tap(array => array.pop())
          .get(0)
          .value()

        acc[dataKey] = {
          ...value,
          label,
          dataPath: dataKey,
        }
      }
      return acc
    }, {})

  const contentSetMetrics = Object.entries(filteredMetrics).map(([metricId, metricData]) => {
    const { dataPath, label, format, category } = metricData
    return ({
      id: metricId,
      label: label || metricId,
      category: selectedCategory || category,
      dataPath,
      format: format || VALUE_FORMATS.NUMBER,
    })
  })
  return reorderByCategory(contentSetMetrics)
}

// Always filter out rebalanced and delta columns from all performance metrics
// and filter out start/comparison and delta columns for end/start comparison
export function getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison) {
  return (modeColumns) => {
    const filteredColumns = modeColumns.length > 1 && modeColumns.includes("rebalanced") ? modeColumns.filter(column => !["rebalanced", "delta"].includes(column)) : modeColumns
    if (isEndVsStartDateComparison) {
      return ["original"]
    }
    return filteredColumns
  }
}

export function getRiskModelMetrics(modelMetadata, isOpPro) {
  if (!modelMetadata) {
    return []
  }
  return compact([
    isOpPro ? null : { id: "risk", name: "Risk", metricOptions: [{ ...METRIC_OPTIONS.default, format: VALUE_FORMATS.PERCENT }], canAggregateCategories: true },
    { id: "exposure", name: "Exposure", metricOptions: METRIC_OPTIONS.exposure },
    isOpPro ? null : { id: "predictedFactorSensitivity", name: "Predicted Factor Sensitivity", metricOptions: METRIC_OPTIONS.factorSensitivity },
    { id: "historicalFactorSensitivity", name: "Historical Factor Sensitivity", metricOptions: METRIC_OPTIONS.factorSensitivity },
    { id: "performance", name: "Performance", metricOptions: METRIC_OPTIONS.performance, canAggregateCategories: true },
  ])
}

export function recursiveReduce(items, key = "", category = null, label = null) {
  return items.reduce((acc, item) => {
    const currentKey = compact([key, item.id]).join(".")
    if ((item.items || item.metricTypeOptions)) {
      return {
        ...acc,
        ...recursiveReduce(
          (item.items || item.metricTypeOptions),
          currentKey,
          item.items ? item.id : category,
          item.metricTypeOptions ? item.label : label,
        ),
      }
    }

    let compositeLabel = label
    if (label && item.label) {
      compositeLabel = `${compositeLabel} (${item.label})`
    } else if (item.label) {
      compositeLabel = item.label
    }

    const metricCategory = chain(currentKey)
      .split(".")
      .first()
      .toUpper()
      .value()

    return {
      ...acc,
      [currentKey]: {
        ...item,
        label: compositeLabel,
        metricCategory,
        ...(category && { category }),
      },
    }
  }, {})
}

export function getDefaultMetricDisplay(metricCategory) {
  const options = METRIC_OPTIONS[metricCategory] || []
  const defaultMetric = options.find(({ isDefault }) => isDefault)
  return defaultMetric?.id || options[0]?.id
}
